import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { useTheme } from '../lib/theme';
import { getBuckets, getCategories } from '../lib/api';
import { groupByBucket } from '../lib/categoryGrouping';
import CreateCategoryModal from './manage-categories/CreateCategoryModal';
import RenameCategoryModal from './manage-categories/RenameCategoryModal';
import DeleteCategoryModal from './manage-categories/DeleteCategoryModal';

export default function ManageCategories() {
  const { colors } = useTheme();
  const [cats, setCats] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, bs] = await Promise.all([
        getCategories(),
        getBuckets().catch(() => []),
      ]);
      setCats(r.categories);
      setBuckets(bs);
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(null), 2000); };

  const presets = useMemo(() => cats.filter((c) => c.is_preset && !c.is_special), [cats]);
  const custom = useMemo(() => cats.filter((c) => !c.is_preset), [cats]);
  const grouped = useMemo(
    () => groupByBucket(cats, buckets, { includeEmpty: true, filter: (c) => !c.is_special }),
    [cats, buckets],
  );

  return (
    <div className="rounded-xl border overflow-hidden" data-testid="manage-categories"
      style={{ backgroundColor: colors.surface, borderColor: colors.border }}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b"
        style={{ borderColor: colors.border }}
      >
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.text }}>Categories</p>
          <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
            {presets.length} preset · {custom.length} custom · grouped by account type
          </p>
        </div>
        <button
          data-testid="mc-add-btn"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
          style={{ backgroundColor: colors.accent }}
        >
          <Plus size={14} /> Add custom
        </button>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center">
          <div className="w-6 h-6 border-2 rounded-full animate-spin"
            style={{ borderColor: colors.border, borderTopColor: colors.accent }}
          />
        </div>
      ) : (
        <div className="p-5 space-y-4">
          <p className="text-[11px]" style={{ color: colors.textMuted }}>
            Preset names are locked for QuickBooks alignment. Click any category to change its account type.
          </p>
          {grouped.map((row) => (
            <div key={row.bucket} data-testid={`mc-bucket-section-${row.bucket}`}>
              <div className="flex items-center gap-2 mb-2 pb-1 border-b" style={{ borderColor: colors.border }}>
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: row.color }}
                />
                <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: colors.text }}>
                  {row.label}
                </p>
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: colors.bg, color: colors.textMuted, border: `1px solid ${colors.border}` }}
                >
                  {row.categories.length}
                </span>
              </div>
              {row.categories.length === 0 ? (
                <p className="text-[11px] pl-4 pb-2" style={{ color: colors.textMuted, fontStyle: 'italic' }}>
                  No categories in {row.label} yet
                </p>
              ) : (
                <div className="flex flex-col gap-1">
                  {row.categories.map((c) => {
                    const isPreset = !!c.is_preset;
                    return (
                      <div
                        key={c.id}
                        data-testid={isPreset ? `mc-preset-${c.id}` : `mc-custom-${c.id}`}
                        className="flex items-center justify-between px-3 py-1.5 rounded-lg"
                        style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                      >
                        <button
                          onClick={() => setRenameTarget(c)}
                          className="flex items-center gap-2 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                          title={isPreset ? 'Edit account type' : 'Edit category'}
                          style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
                        >
                          <span className="text-sm font-medium truncate" style={{ color: colors.text }}>
                            {c.name}
                          </span>
                          {isPreset ? (
                            <span className="text-[9px] font-semibold uppercase tracking-wider"
                              style={{ color: colors.textMuted }}
                            >
                              preset
                            </span>
                          ) : null}
                        </button>
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            data-testid={`mc-rename-${c.id}`}
                            onClick={() => setRenameTarget(c)}
                            className="p-1 rounded hover:opacity-70"
                            style={{ color: colors.textMuted }}
                            title={isPreset ? 'Edit account type' : 'Edit category'}
                          >
                            <Edit2 size={13} />
                          </button>
                          {!isPreset ? (
                            <button
                              data-testid={`mc-delete-${c.id}`}
                              onClick={() => setDeleteTarget(c)}
                              className="p-1 rounded hover:opacity-70"
                              style={{ color: colors.error }}
                            >
                              <Trash2 size={13} />
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {createOpen ? (
        <CreateCategoryModal
          colors={colors}
          onClose={() => setCreateOpen(false)}
          onCreated={(name) => { setCreateOpen(false); showToast(`Created ${name}`); load(); }}
        />
      ) : null}

      {renameTarget ? (
        <RenameCategoryModal
          colors={colors}
          category={renameTarget}
          onClose={() => setRenameTarget(null)}
          onDone={(payload) => {
            setRenameTarget(null);
            const label = payload?.bucketLabel;
            const name = payload?.name || (typeof payload === 'string' ? payload : renameTarget.name);
            showToast(label ? `Moved ${name} to ${label}` : `Updated ${name}`);
            load();
          }}
        />
      ) : null}

      {deleteTarget ? (
        <DeleteCategoryModal
          colors={colors}
          category={deleteTarget}
          allCategories={cats}
          onClose={() => setDeleteTarget(null)}
          onDone={(n) => { setDeleteTarget(null); showToast(n ? `Deleted · ${n} moved` : 'Deleted'); load(); }}
        />
      ) : null}

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-white text-sm z-50"
          style={{ backgroundColor: colors.text }}
          data-testid="mc-toast"
        >
          {toast}
        </div>
      ) : null}
    </div>
  );
}
